/**
 * What a vehicle can actually open, and through which hardware.
 *
 * The question this answers is the one the portaria asks out loud: *"why doesn't this car open the
 * garage?"* Until now the only way to answer it was to open the owner's profile, read their access
 * profile, and know by heart which device types can identify a car at all.
 *
 * Three facts make that non-obvious, and all three are encoded here:
 *
 *  1. **A vehicle has no permissions of its own.** It inherits them from its owner, or — for a
 *     unit-linked car with no owner — from the unit. If it has neither, it has no access, and that
 *     is why syncing it does nothing (see `vehicleAccessSource`).
 *  2. **A deny zone is not access.** The backend documents "deny profiles always override allow",
 *     and the sync path already refuses to write a tag into a denied zone. Listing such a zone as
 *     access would describe the opposite of what happens at the gate.
 *  3. **Most devices cannot identify a car.** A facial reader in the same zone is irrelevant to a
 *     vehicle; only an RFID reader, a Guarita integration (tag) or an LPR camera (plate) can. A
 *     zone full of readers the car cannot use is exactly what makes the gate "mysteriously" refuse
 *     it.
 *
 * The classification mirrors `vehicleRfidSync.parseZoneDevices` field for field, because the list
 * this screen shows must be the list the sync actually writes to — a screen that promises a device
 * the sync skips is worse than no screen.
 */

export type VehicleAccessDeviceKind = 'rfid' | 'guarita' | 'lpr' | 'lpr_cloud' | 'other';

export interface VehicleAccessDevice {
  id: string;
  name: string;
  kind: VehicleAccessDeviceKind;
  /** Can this device identify a VEHICLE — by tag or by plate? */
  readsVehicle: boolean;
  /**
   * Does syncing write the vehicle into this device's own allow-list?
   *
   * False for an Alphadigi camera: it reads the plate but holds no list, reporting each read to
   * the cloud and opening on the response. It grants access without ever being synced, so showing
   * it as "not synced" would read as a fault.
   */
  syncTarget: boolean;
  /** The device's own type string, kept for the "outros dispositivos" explanation. */
  deviceType: string;
}

export interface VehicleAccessZone {
  id: string;
  name: string;
  /** An explicit deny profile. Listed, but as access REMOVED — never as access granted. */
  denied: boolean;
  devices: VehicleAccessDevice[];
}

const RFID_TYPES = new Set(['rfid_reader', 'controlid_rfid']);
const LPR_TYPES = new Set(['hikvision_lpr', 'lpr']);

/** How a device type is classified, and whether a car can be identified by it. */
export function classifyAccessDevice(deviceType: string): {
  kind: VehicleAccessDeviceKind;
  readsVehicle: boolean;
  syncTarget: boolean;
} {
  const t = (deviceType || '').toLowerCase();
  if (RFID_TYPES.has(t)) return { kind: 'rfid', readsVehicle: true, syncTarget: true };
  if (t === 'guarita') return { kind: 'guarita', readsVehicle: true, syncTarget: true };
  if (LPR_TYPES.has(t)) return { kind: 'lpr', readsVehicle: true, syncTarget: true };
  // Reads plates, but decides in the cloud from the event rather than from a list on the device.
  if (t === 'alphadigi_lpr') return { kind: 'lpr_cloud', readsVehicle: true, syncTarget: false };
  return { kind: 'other', readsVehicle: false, syncTarget: false };
}

/** Human label for a device kind, as the portaria would name it. */
export function accessDeviceKindLabel(kind: VehicleAccessDeviceKind): string {
  switch (kind) {
    case 'rfid': return 'Leitor RFID';
    case 'guarita': return 'Guarita';
    case 'lpr': return 'Câmera LPR';
    case 'lpr_cloud': return 'Câmera LPR (nuvem)';
    default: return 'Não identifica veículo';
  }
}

/**
 * The zones and devices behind a vehicle's access, from an `effective-access-zones` payload.
 *
 * Denied zones are kept in the list rather than dropped: "this car is explicitly blocked from the
 * garage" and "nobody ever gave it the garage" are different answers, and only one of them is
 * something to go and fix.
 */
export function buildVehicleAccess(zones: any[]): VehicleAccessZone[] {
  return (zones || []).map((zone, index) => {
    const devices: VehicleAccessDevice[] = (zone?.devices || []).map((device: any) => {
      const deviceType = device?.device_type || device?.type || '';
      const { kind, readsVehicle, syncTarget } = classifyAccessDevice(deviceType);
      return {
        id: device?.id || `${zone?.id || index}-${device?.name || 'device'}`,
        name: device?.name || 'Dispositivo',
        kind,
        readsVehicle,
        syncTarget,
        deviceType,
      };
    });

    return {
      id: zone?.id || `zone-${index}`,
      name: zone?.name || zone?.zone_name || 'Zona',
      denied: String(zone?.access_type || '').toLowerCase() === 'deny',
      devices,
    };
  });
}

/**
 * The zones worth showing on a VEHICLE screen: those with at least one device that can identify a
 * car, with the other devices stripped out.
 *
 * The first version listed every zone and every device, marking the irrelevant ones "não identifica
 * veículo". On a real condomínio that was nine cards of facial readers around the single antenna
 * that matters — the answer was in there, buried. A vehicle screen lists tag readers and LPR
 * cameras; what is left over is reported as one line, not as eight cards (see
 * [VehicleAccessSummary.zonesWithoutReader]), so nothing is hidden and nothing is shouted.
 */
export function vehicleReadableZones(zones: VehicleAccessZone[]): VehicleAccessZone[] {
  return zones
    .map((zone) => ({ ...zone, devices: zone.devices.filter((d) => d.readsVehicle) }))
    .filter((zone) => zone.devices.length > 0);
}

export interface VehicleAccessSummary {
  /** Zones the vehicle may enter — denied zones excluded. */
  allowedZones: number;
  /** Zones explicitly blocked for it. */
  deniedZones: number;
  /** Devices in allowed zones that can identify this car. */
  readers: number;
  /** Of those, the ones a sync writes into. */
  syncTargets: number;
  /**
   * Allowed zones that hold NO device able to identify a car — pedestrian gates, facial-only
   * doors. Counted rather than listed: it is the difference between "the car has nine zones" and
   * "the car can be recognised in one of them", which is the question being asked.
   */
  zonesWithoutReader: number;
}

export function vehicleAccessSummary(zones: VehicleAccessZone[]): VehicleAccessSummary {
  const allowed = zones.filter((z) => !z.denied);
  const readers = allowed.flatMap((z) => z.devices).filter((d) => d.readsVehicle);
  const withReader = allowed.filter((z) => z.devices.some((d) => d.readsVehicle));
  return {
    allowedZones: allowed.length,
    deniedZones: zones.length - allowed.length,
    readers: readers.length,
    syncTargets: readers.filter((d) => d.syncTarget).length,
    zonesWithoutReader: allowed.length - withReader.length,
  };
}

export type VehicleAccessSourceKind = 'person' | 'unit' | 'none';

export interface VehicleAccessSourceInfo {
  kind: VehicleAccessSourceKind;
  /** One line explaining WHOSE access this is. */
  label: string;
  /** The id to query `effective-access-zones` for, or null when there is nothing to query. */
  queryId: string | null;
}

/**
 * Whose permissions this vehicle rides on.
 *
 * The owner first, the unit second — the same order `syncVehicleRfid` resolves, so the screen and
 * the sync never disagree about where the access came from. A vehicle with neither is the case
 * worth stating outright: nothing is broken, there is simply nobody to inherit from, and no amount
 * of syncing will change that.
 */
export function vehicleAccessSource(vehicle: {
  person_id?: string | null;
  owner_name?: string | null;
  person?: { first_name?: string; last_name?: string } | null;
  unit_id?: string | null;
  unit?: { id?: string; number?: string } | null;
} | null | undefined): VehicleAccessSourceInfo {
  if (!vehicle) return { kind: 'none', label: 'Sem vínculo', queryId: null };

  if (vehicle.person_id) {
    const name = vehicle.person
      ? `${vehicle.person.first_name || ''} ${vehicle.person.last_name || ''}`.trim()
      : (vehicle.owner_name || '').trim();
    return {
      kind: 'person',
      label: name ? `Herdado de ${name}` : 'Herdado do proprietário',
      queryId: vehicle.person_id,
    };
  }

  const unitId = vehicle.unit_id || vehicle.unit?.id || null;
  if (unitId) {
    const number = vehicle.unit?.number;
    return {
      kind: 'unit',
      label: number ? `Herdado da unidade ${number}` : 'Herdado da unidade',
      queryId: unitId,
    };
  }

  return {
    kind: 'none',
    label: 'Sem vínculo — o veículo não herda acesso de ninguém',
    queryId: null,
  };
}
